import { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  FormFeedback,
  Input,
  Label,
  Form,
} from "reactstrap";

import { withRouter, Link, useHistory } from "react-router-dom";

import * as Yup from "yup";
import { useFormik } from "formik";

import { useForgetPasswordMutation } from "../../store/api/authApi";

import { ParticlesAuth } from "../AuthenticationInner";
import { useTranslation } from "react-i18next";
import { ForgetPasswordRequest, ParsedError } from "../../interfaces";
import { ActivityButton, AppLogo } from "../../components/Common";
import LanguageDropdown from "../../components/Common/LanguageDropdown";
import { ErrorHandlingTypeEnum, VerificationChannelEnum } from "../../enums";
import { EncryptionService } from "../../services";

const ForgetPasswordPageComponent = (props: any) => {
  const history = useHistory();
  const { t } = useTranslation();
  document.title = `${t("documentTitle", { pageName: "forgetPassword" })}`;

  const [
    forgetPassword,
    {
      isLoading: isLoadingForgetPassword,
      isError: isErrorForgotPassword,
      error: errorForgotPassword,
      isSuccess: isSuccessForgotPassword,
      data: dataForgotPassword,
    },
  ] = useForgetPasswordMutation();

  const [forgetPasswordRequest] = useState<ForgetPasswordRequest>({
    countryCode: process.env.REACT_APP_COUNTRY_CODE || "965",
    phoneNumber: "",
    channel: VerificationChannelEnum.WHATSAPP,
  });

  //#region form
  const form = useFormik({
    enableReinitialize: true,
    initialValues: {
      countryCode: forgetPasswordRequest.countryCode,
      phoneNumber: forgetPasswordRequest.phoneNumber,
    },
    validationSchema: Yup.object({
      phoneNumber: Yup.string().required(
        t("isNotEmpty", {
          displayName: "phoneNumber",
        }),
      ),
    }),

    onSubmit: async (values) => {
      await forgetPassword({
        ...values,
        channel: VerificationChannelEnum.WHATSAPP,
      });
    },
  });

  useEffect(() => {
    let _error: ParsedError | null = null;
    if (!isLoadingForgetPassword && isErrorForgotPassword)
      _error = errorForgotPassword as ParsedError;
    if (_error?.type == ErrorHandlingTypeEnum.FORM) {
      form.setErrors(_error.error);
    }
  }, [isLoadingForgetPassword]);

  useEffect(() => {
    if (
      !isLoadingForgetPassword &&
      isSuccessForgotPassword &&
      dataForgotPassword
    ) {
      const encPhone = EncryptionService.encrypt(
        `${dataForgotPassword.phone.countryCode}-${dataForgotPassword.phone.phoneNumber}`,
      );
      history.push(`/verification/${encPhone}`);
    }
  }, [isLoadingForgetPassword, isSuccessForgotPassword, dataForgotPassword]);

  //#endregion

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-1">
                    <AppLogo />
                    <h5 className="text-primary mt-2">
                      {t("forgetPassword?")}
                    </h5>
                    <p className="text-muted">
                      {t("forgetPasswordSubHeading")}
                    </p>
                  </div>
                  <div className="mt-4">
                    <div className="justify-content-center align-items-center d-flex">
                      <LanguageDropdown />
                    </div>

                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        form.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-3">
                        <Label htmlFor="phoneNumber" className="form-label">
                          {t("phoneNumber")}
                        </Label>
                        <span className="text-danger">*</span>
                        <Input
                          autoComplete="off"
                          id="phoneNumber"
                          name="phoneNumber"
                          className="form-control"
                          placeholder={t("enterPhoneNumber")}
                          type="text"
                          onBlur={form.handleBlur}
                          value={form.values.phoneNumber || ""}
                          invalid={
                            form.touched.phoneNumber && form.errors.phoneNumber
                              ? true
                              : false
                          }
                          maxLength={12}
                          onChange={(e) => {
                            e.preventDefault();
                            const { value } = e.target;
                            const regex =
                              /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                            if (regex.test(value.toString())) {
                              form.setValues({
                                ...form.values,
                                phoneNumber: value,
                              });
                            } else {
                              form.setValues({
                                ...form.values,
                                phoneNumber: "",
                              });
                            }
                          }}
                        />
                        {form.touched.phoneNumber && form.errors.phoneNumber ? (
                          <FormFeedback type="invalid">
                            {form.errors.phoneNumber as string}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mt-4">
                        <ActivityButton
                          title="sendVerificationCode"
                          inProgressTitle="sending"
                          color="success"
                          className="btn btn-success w-100"
                          type="submit"
                          inProgress={isLoadingForgetPassword}
                          disabled={isLoadingForgetPassword}
                        ></ActivityButton>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <p className="mb-0 text-white">
                  {t("waitIRememberMyPassword")}...{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-white text-decoration-underline"
                  >
                    {" "}
                    {t("clickHere")}{" "}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

export const ForgetPasswordPage = withRouter(ForgetPasswordPageComponent);
